import { store, type AppState } from "../core/store";
import { GameState } from "../../shared/types";
import { mountJoinForm, mountWaitingRoom } from "./lobby";
import { mountWordSelectionArtist, mountWordSelectionWaiting } from "./wordSelection";
import { mountCanvasView } from "./canvasView";
import { mountAnnouncement } from "./turnAnnouncement";
import { mountVictory } from "./victory";
import { mountCCCSelection } from "./cccSelection";

type ViewKey =
  | "join"
  | "waiting-room"
  | "announcement"
  | "word-select-artist"
  | "word-select-waiting"
  | "canvas"
  | "ccc-selection"
  | "victory";

function computeViewKey(state: AppState): ViewKey {
  const gameState = state.snapshot?.state ?? GameState.WAITING_ROOM;
  // An anonymous spectator has no Player identity at all, so once the game
  // returns to the lobby there's nothing for them to see there (no ready-up,
  // no settings to own) - they fall back to the join screen, same as anyone
  // else who hasn't decided yet. See lobby.ts's mountJoinForm for the other
  // half of this: the "watch anonymously" choice only appears in the first
  // place while a game is already in progress.
  const watching = state.joined || (state.isAnonymousSpectator && gameState !== GameState.WAITING_ROOM);
  if (!watching) return "join";

  switch (gameState) {
    case GameState.WAITING_ROOM:
      return "waiting-room";
    case GameState.ROUND_START:
    case GameState.ARTIST_SELECTION:
    case GameState.NEXT_ARTIST:
    case GameState.SCORE_CALCULATION:
      return "announcement";
    case GameState.WORD_SELECTION:
      return state.myPlayerId === state.snapshot?.currentArtistId ? "word-select-artist" : "word-select-waiting";
    case GameState.DRAWING:
      return "canvas";
    case GameState.CCC_SELECTION:
      return "ccc-selection";
    case GameState.VICTORY_CEREMONY:
    case GameState.RETURN_TO_WAITING_ROOM:
      return "victory";
    default:
      return "waiting-room";
  }
}

function mountView(key: ViewKey, container: HTMLElement): () => void {
  switch (key) {
    case "join":
      return mountJoinForm(container);
    case "waiting-room":
      return mountWaitingRoom(container);
    case "announcement":
      return mountAnnouncement(container);
    case "word-select-artist":
      return mountWordSelectionArtist(container);
    case "word-select-waiting":
      return mountWordSelectionWaiting(container);
    case "canvas":
      return mountCanvasView(container);
    case "ccc-selection":
      return mountCCCSelection(container);
    case "victory":
      return mountVictory(container);
  }
}

export function initMainArea(): void {
  const container = document.getElementById("main-area")!;
  let currentKey: ViewKey | null = null;
  let unmount: (() => void) | null = null;

  const sync = (state: AppState): void => {
    const key = computeViewKey(state);
    if (key === currentKey) return;
    unmount?.();
    container.innerHTML = "";
    currentKey = key;
    unmount = mountView(key, container);
  };

  sync(store.getState());
  store.subscribe(sync);
}
